import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ApiError } from '@/domains/api';
import { authApi } from '../api';
import { useAuth } from '../auth-context';
import { HOME_ROUTE, LOGIN_ROUTE } from '../constants';
import { tokenFromLink } from '../link-token';
import { AuthShell } from './auth-shell';
import { DoorBackLink } from './door-back-link';
import { VerificationRequest } from './verification-request';

type Outcome = 'ready' | 'confirming' | 'confirmed' | 'refused' | 'failed';

/**
 * `/email/verify` — where survey-api's address confirmation mail links (its self-service plan,
 * R11, R13): `POST /auth/email/verification { token }`, anonymous.
 *
 * **The token rides in the fragment** (`tokenFromLink`): nginx logs the request line in the
 * default `combined` format, and a fragment is never sent — not to the server, not as a
 * `Referer`. `?token=` is still read, second, for a link rebuilt by hand; survey-api never mints
 * one. It is not stripped once read, so a reload still has it.
 *
 * **It confirms on a press, never on load** — the owner's ruling. A mail's links are opened by
 * more than their reader: link scanners (Outlook's Safe Links, Defender) follow them in a
 * sandbox that runs the page's script, and a page that redeemed on load would spend the token
 * there, leaving the person who clicks it a "this link cannot be used". A press is what a
 * scanner does not do.
 *
 * **Two answers, and the page says two things.** 204 is a confirmation — including of an
 * address confirmed another way since, which survey-api still answers 204 and spends. An empty
 * 400 is every other token alike: invented, expired, already spent, replaced by a newer link,
 * another mail's token pasted here. survey-api makes them one answer on purpose, and the page
 * does not split them — no wording here distinguishes one from another, by title or by anything
 * else.
 * What it does add is the one fact a signed-in visitor's own account can supply: whether *their*
 * address is confirmed now, read again from `/auth/me` after the answer, since a scanner that
 * spent the token confirmed it all the same.
 *
 * **Anonymous, and it grants nothing.** Mounted beside `/login`, outside `ProtectedRoute`,
 * because the mail is read wherever it is read. It issues no session and ends none, so a session
 * this browser holds is neither sent nor dropped: the request goes without a bearer, and the only
 * use made of a session is to offer a new link to its owner and re-read their account after the
 * answer.
 */
export function VerifyEmailPage() {
  const { hash, search } = useLocation();
  const { isAuth, user, refreshUser } = useAuth();
  const [outcome, setOutcome] = React.useState<Outcome>('ready');

  // Fragment first, query second — `tokenFromLink` says why only the first is minted.
  const token = tokenFromLink(hash, search).trim();

  const confirm = async () => {
    setOutcome('confirming');
    try {
      await authApi.verifyEmail(token);
      setOutcome('confirmed');
    } catch (error) {
      setOutcome(error instanceof ApiError && error.status === 400 ? 'refused' : 'failed');
    }
    // Either answer can change what this account's own `/auth/me` says — the prompt under the
    // header reads it, and so does the refusal below. Not held to `isAuth`: a press while the
    // session's first read is out would skip it, and `refreshUser` is a no-op with no session.
    await refreshUser();
  };

  // `/` lands by kind, so the way on names no portal: a Team Portal account follows its own links
  // here too.
  const onward = isAuth ? HOME_ROUTE : LOGIN_ROUTE;
  const verifyTitle = titleOf(token, outcome);
  const nextStep = (
    <NextStep
      isAuth={isAuth}
      verified={user?.emailVerified ?? false}
      email={user?.email ?? ''}
    />
  );

  return (
    <AuthShell title={verifyTitle}>
      {!token ? (
        <>
          <h1 className="text-2xl text-[var(--color-royal-blue)]">This link is incomplete</h1>
          <p className="mt-2 mb-6 text-sm text-muted-foreground">
            The confirmation link has lost its code — a mail client may have broken it across
            two lines. Open it again from the mail, or ask for a new one.
          </p>
          {nextStep}
        </>
      ) : outcome === 'confirmed' ? (
        <>
          <h1 className="text-2xl text-[var(--color-royal-blue)]">
            Your email address is confirmed
          </h1>
          <p className="mt-2 mb-6 text-sm text-muted-foreground">
            Thank you — that is all it takes. The link is used up and will not work again.
          </p>
          <Button asChild className="w-full">
            <Link to={onward}>{isAuth ? 'Continue' : 'Sign in'}</Link>
          </Button>
        </>
      ) : outcome === 'refused' ? (
        <>
          <h1 className="text-2xl text-[var(--color-royal-blue)]">This link cannot be used</h1>
          <p className="mt-2 mb-6 text-sm text-muted-foreground">
            It is not valid or has expired — it may have been used already, or replaced by a
            newer one.
          </p>
          {nextStep}
        </>
      ) : (
        <>
          <h1 className="text-2xl text-[var(--color-royal-blue)]">
            Confirm your email address
          </h1>
          <p className="mt-2 mb-6 text-sm text-muted-foreground">
            This confirms that the address this link was mailed to is yours. It works once,
            and nothing else about your account changes.
          </p>
          {outcome === 'failed' && (
            // Not the 400: an answer that never arrived proves nothing about the token, which
            // may still be good — so the button stays, and a spent one answers 400 next time.
            <p role="alert" className="mb-4 text-sm font-medium text-destructive">
              Could not confirm your address — the request did not complete. Try again.
            </p>
          )}
          <Button
            type="button"
            className="w-full"
            disabled={outcome === 'confirming'}
            onClick={() => void confirm()}
          >
            {outcome === 'confirming' ? 'Confirming…' : 'Confirm my address'}
          </Button>
        </>
      )}

      {/* Not under a confirmation, whose one button already goes the same way. */}
      {outcome !== 'confirmed' && <DoorBackLink isAuth={isAuth} />}
    </AuthShell>
  );
}

/** The tab's title — the heading each state opens with. */
function titleOf(token: string, outcome: Outcome): string {
  if (!token) return 'This link is incomplete';
  if (outcome === 'confirmed') return 'Your email address is confirmed';
  if (outcome === 'refused') return 'This link cannot be used';
  return 'Confirm your email address';
}

/**
 * What a visitor holding a link that cannot be used can do next — which depends on who they are
 * here, and on nothing about the link. Signed in, their own account answers: confirmed already
 * (a scanner, an earlier click, another link), or a new link to ask for. Signed out, the way to a
 * new link is signing in, where the prompt offers one.
 */
function NextStep({
  isAuth,
  verified,
  email
}: {
  isAuth: boolean;
  verified: boolean;
  email: string;
}) {
  if (!isAuth) {
    return (
      <div className="grid gap-3">
        <p className="text-sm">
          Sign in to ask for a new link — it is offered at the top of every screen.
        </p>
        <Button asChild className="w-full">
          <Link to={LOGIN_ROUTE}>Sign in</Link>
        </Button>
      </div>
    );
  }

  if (verified) {
    return (
      <p role="status" className="text-sm">
        Your address, <strong className="break-words">{email}</strong>, is confirmed already —
        there is nothing more to do.
      </p>
    );
  }

  return (
    <div className="grid gap-3">
      <p className="text-sm">
        Ask for a new link — it goes to <strong className="break-words">{email}</strong>.
      </p>
      <VerificationRequest email={email} />
    </div>
  );
}
